import { CreateAssistant } from '../CreateAssistantSlide';
import React, { useState, useEffect } from 'react';
import { Button, Typography, BaseIcon, Tooltip } from '@scale/scaleui';
import RunOneOffScreeningJobModal from './RunOneOffScreeningJobModal';
import { ResumeScreeningJob } from '@scale/shared-types';
import {
  createResumeScreeningJob,
  updateResumeScreeningJob,
  getResumeScreeningJob,
} from 'react-scale/corp/lib/api/growth';
import SAlert from 'react-s-alert';
import { Link, useParams } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { useQueryClient } from 'react-query';

const ResumeScreeningJobPage: React.FC<{}> = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const queryClient = useQueryClient();

  const [jobConfig, setJobConfig] = useState<Partial<ResumeScreeningJob>>({});

  const [runOneOffScreeningJobModalOpen, setRunOneOffScreeningJobModalOpen] = useState(false);

  const [isJobUpdated, setIsJobUpdated] = useState(false);

  useEffect(() => {
    async function fetchJob() {
      if (jobId) {
        const campaign = await getResumeScreeningJob(jobId);
        setJobConfig(campaign as Partial<ResumeScreeningJob>);
      }
    }
    fetchJob();
  }, [jobId]);

  const handleAddQuestion = () => {
    const updatedQuestions = [...(jobConfig?.questionsToAskGPT || []), ''];
    const updatedConfig = { ...jobConfig, questionsToAskGPT: updatedQuestions };
    setJobConfig(updatedConfig);
    setIsJobUpdated(true);
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...(jobConfig?.questionsToAskGPT || [])];
    updatedQuestions.splice(index, 1);
    const updatedConfig = { ...jobConfig, questionsToAskGPT: updatedQuestions };
    setJobConfig(updatedConfig);
    setIsJobUpdated(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
    index?: number,
  ) => {
    const { name, value } = e.target;
    if (name === 'questionsToAskGPT' && index !== undefined) {
      const updatedQuestions = [...(jobConfig?.questionsToAskGPT || [])];
      updatedQuestions[index] = value;
      const updatedConfig = { ...jobConfig, questionsToAskGPT: updatedQuestions };
      setJobConfig(updatedConfig);
    } else {
      const updatedConfig = { ...jobConfig, [name]: value };
      setJobConfig(updatedConfig);
    }
    setIsJobUpdated(true);
  };

  const handleRequiresAllQuestionsPassedChange = (requiresAllQuestionsPassed: boolean) => {
    const updatedConfig = { ...jobConfig, requiresAllQuestionsPassed };
    setJobConfig(updatedConfig);
    setIsJobUpdated(true);
  };

  const saveJob = async () => {
    let updatedConfig;
    try {
      if (jobConfig._id) {
        updatedConfig = await updateResumeScreeningJob(jobConfig._id, jobConfig);
      } else {
        updatedConfig = await createResumeScreeningJob(jobConfig);
      }
      if (updatedConfig) {
        setJobConfig(updatedConfig);
        setIsJobUpdated(false);
        queryClient.invalidateQueries(['listResumeScreeningJobs']);
        SAlert.success('Resume Screening job saved');
      } else {
        SAlert.error('Error saving job config');
      }
    } catch (error) {
      if (error.response) {
        const errorMessage = error.response.message || error.response.error;
        SAlert.error(errorMessage);
      } else {
        SAlert.error('Error saving job config', error);
      }
    }
  };

  const handleLastRunAtChange = (lastRunAt: Date) => {
    setJobConfig({ ...jobConfig, lastRunAt });
  };

  const runJob = async () => {
    setRunOneOffScreeningJobModalOpen(true);
  };

  const closeOneOffScreeningJobModal = () => {
    setRunOneOffScreeningJobModalOpen(false);
  };

  const [isCreateAssistantOpen, setIsCreateAssistantOpen] = useState(true);

  return (
    <div className="scaleui scale-tailwind flex flex-col h-full w-full items-center">
      <div className="sticky flex w-full flex-col bg-neutral-0 border-b border-b-neutral-200 border-b-2 justify-between p-1">
        <Link to={Routes.ResumeScreening} className="flex gap-3 items-center">
          <Button className="w-fit bg-transparent shadow-none" variant="neutral">
            <BaseIcon contrastText edge="start" icon={['fas', 'arrow-left']} variant="neutral" />
            Exit
          </Button>
        </Link>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <Typography.Paragraph1 className="text-neutral-900 ml-2 font-medium">
              {jobConfig._id ? 'Update Resume Screening Job' : 'Create Resume Screening Job'}
            </Typography.Paragraph1>
            <Typography.Paragraph2 className="text-neutral-800 px-2 py-1 mb-2">
              Run a resume screening job on a list of emails from corp using LLMs.
            </Typography.Paragraph2>
          </div>
        </div>
      </div>
      <div className="p-3 flex flex-col bg-neutral-0 w-[50%] h-full ">
        <CreateAssistant
          selectedJobConfig={jobConfig}
          handleInputChange={handleInputChange}
          handleAddQuestion={handleAddQuestion}
          handleRemoveQuestion={handleRemoveQuestion}
          isCreateAssistantOpen={isCreateAssistantOpen}
          toggleCreateAssistantOpen={() => {
            setIsCreateAssistantOpen(!isCreateAssistantOpen);
          }}
          handleRequiresAllQuestionsPassedChange={handleRequiresAllQuestionsPassedChange}
          isOneOffJob={true}
        />
        <div className="flex flex-row-reverse items-start gap-2">
          <Tooltip
            side="top"
            target={
              <div>
                <Button
                  onClick={() => runJob()}
                  disabled={
                    !jobConfig.assistantDescription ||
                    !jobConfig.questionsToAskGPT?.length ||
                    jobConfig.questionsToAskGPT.some(q => !q) ||
                    !jobConfig._id ||
                    isJobUpdated
                  }
                >
                  Run One Off Screening
                </Button>
              </div>
            }
            text={
              !jobConfig.assistantDescription ||
              !jobConfig.questionsToAskGPT?.length ||
              jobConfig.questionsToAskGPT.some(q => !q) ||
              !jobConfig._id
                ? 'Please fill out all fields'
                : isJobUpdated
                ? 'Please save the job first'
                : ''
            }
          />
          <Tooltip
            side="top"
            target={
              <div>
                <Button
                  onClick={() => saveJob()}
                  disabled={
                    !jobConfig.assistantDescription ||
                    !jobConfig.questionsToAskGPT?.length ||
                    jobConfig.questionsToAskGPT.some(q => !q) ||
                    !isJobUpdated
                  }
                >
                  Save
                </Button>
              </div>
            }
            text={
              !jobConfig.assistantDescription ||
              !jobConfig.questionsToAskGPT?.length ||
              jobConfig.questionsToAskGPT.some(q => !q)
                ? 'Please fill out all fields'
                : (!isJobUpdated && 'No changes to save') || ''
            }
          />
        </div>
      </div>

      {jobConfig && (
        <RunOneOffScreeningJobModal
          open={runOneOffScreeningJobModalOpen}
          onClose={closeOneOffScreeningJobModal}
          jobConfig={jobConfig}
          handleLastRunAtChange={handleLastRunAtChange}
        />
      )}
    </div>
  );
};

export default ResumeScreeningJobPage;
